import { ConnectionsStateService } from '@core/services/connections-state.service';
import { getAllConnections, AgentConnection } from './agent-connections.utils';

/**
 * Utilidades para mostrar el estado y estilos de conexiones en la interfaz
 */
export class ConnectionsDisplayUtils {
  /**
   * Obtiene las conexiones activas/seleccionadas del agente
   * @param selectedConnections - Array de IDs de conexiones seleccionadas
   * @returns Array de objetos AgentConnection que están seleccionados
   */
  static getActiveConnections(selectedConnections: string[]): AgentConnection[] {
    const allConnections = getAllConnections();
    return allConnections.filter(connection =>
      selectedConnections.includes(connection.id)
    );
  }

  /**
   * Obtiene las clases CSS para el ícono de conexión basado en su estado
   * @param connectionId - ID de la conexión
   * @param connectionsStateService - Servicio de estado de conexiones
   * @returns String con clases Tailwind CSS
   */
  static getConnectionIconClasses(
    connectionId: string,
    connectionsStateService: ConnectionsStateService
  ): string {
    const isConnected = connectionsStateService.isConnectionConnected(connectionId);

    if (isConnected) {
      // Verde cuando está conectado
      return 'text-green-600 dark:text-green-400';
    } else {
      // Gris cuando no está conectado
      return 'text-gray-400 dark:text-gray-600';
    }
  }

  /**
   * Obtiene las clases CSS para el borde de la conexión basado en su estado
   * @param connectionId - ID de la conexión
   * @param connectionsStateService - Servicio de estado de conexiones
   * @returns String con clases Tailwind CSS
   */
  static getConnectionBorderClasses(
    connectionId: string,
    connectionsStateService: ConnectionsStateService
  ): string {
    const isConnected = connectionsStateService.isConnectionConnected(connectionId);

    if (isConnected) {
      // Borde verde cuando está conectado
      return 'border-green-500';
    } else {
      // Borde gris cuando no está conectado
      return 'border-gray-400';
    }
  }

  /**
   * Verifica si una conexión está activa/conectada
   * @param connectionId - ID de la conexión
   * @param connectionsStateService - Servicio de estado de conexiones
   * @returns true si la conexión está activa, false si no
   */
  static isConnectionActive(
    connectionId: string,
    connectionsStateService: ConnectionsStateService
  ): boolean {
    return connectionsStateService.isConnectionConnected(connectionId);
  }

  /**
   * Obtiene un objeto de mapeo entre conexiones y sus estados visuales
   * @param selectedConnections - Array de IDs de conexiones seleccionadas
   * @param connectionsStateService - Servicio de estado de conexiones
   * @returns Objeto con información de visualización para cada conexión
   */
  static getConnectionsDisplayMap(
    selectedConnections: string[],
    connectionsStateService: ConnectionsStateService
  ): Map<string, { isActive: boolean; iconClasses: string; borderClasses: string }> {
    const map = new Map<string, { isActive: boolean; iconClasses: string; borderClasses: string }>();
    
    const activeConnections = this.getActiveConnections(selectedConnections);
    
    activeConnections.forEach(connection => {
      const isActive = this.isConnectionActive(connection.id, connectionsStateService);
      const iconClasses = this.getConnectionIconClasses(connection.id, connectionsStateService);
      const borderClasses = this.getConnectionBorderClasses(connection.id, connectionsStateService);
      
      map.set(connection.id, { isActive, iconClasses, borderClasses });
    });

    return map;
  }
}